import type { ComponentChildren } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { openPanel } from '../store';

interface PanelProps {
  title: string;
  icon: string;
  wide?: boolean;
  children: ComponentChildren;
}

/** Modal card shell shared by every panel: backdrop, title bar, close button, focus. */
export function Panel({ title, icon, wide, children }: PanelProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    return () => previous?.focus?.();
  }, []);

  return (
    <div class="panel-backdrop" onClick={() => openPanel(null)}>
      <div
        ref={ref}
        class={`panel ${wide ? 'panel-wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <header class="panel-header">
          <span class="panel-icon" aria-hidden="true">
            {icon}
          </span>
          <h2>{title}</h2>
          <button class="icon-button close" aria-label="Close" onClick={() => openPanel(null)}>
            ✕
          </button>
        </header>
        <div class="panel-body">{children}</div>
      </div>
    </div>
  );
}

interface TabsProps<T extends string> {
  tabs: ReadonlyArray<{ id: T; label: string }>;
  active: T;
  onChange(id: T): void;
}

export function Tabs<T extends string>({ tabs, active, onChange }: TabsProps<T>) {
  return (
    <div class="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={tab.id === active}
          class={`tab ${tab.id === active ? 'active' : ''}`}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
